/**
 * ContactPage — Public Hybrid Elite landing page with institutional
 * contact form. POSTs directly to /api/public/leads/website-form which
 * triggers the full P5/P6/P7/P8 workflow chain.
 *
 * Route: /contact (wrapped by NewHeader + NewFooter in App.js)
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, Send, Loader2, CheckCircle2, AlertCircle,
  Mail, Phone, MapPin, Globe, Building2, ArrowLeft,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [data, setData] = useState({
    name: '', email: '', phone: '', company: '',
    country: '', role: 'client', industry: '',
    message: '', hp_field: '',  // honeypot
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setData((p) => ({ ...p, [k]: e.target.value }));

  const valid = () =>
    data.name.trim() &&
    /\S+@\S+\.\S+/.test(data.email) &&
    data.company.trim();

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!valid()) {
      setError('Please complete name, email and company.');
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/public/leads/website-form`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Submission failed.');
      }
      setDone(true);
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-[70vh] bg-[#0B0B0B] text-white flex items-center justify-center px-6 py-20"
        data-testid="contact-success-state">
        <div className="max-w-lg text-center">
          <CheckCircle2 className="w-20 h-20 text-emerald-400 mx-auto mb-6" />
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9A86A] mb-2">
            Thank you
          </div>
          <h1 className="text-4xl font-serif mb-4">We received your enquiry</h1>
          <p className="text-slate-300 leading-relaxed">
            A member of our team will reach out within one business day.
            Your message is recorded under our institutional CRM, fully
            audited and compliant with our risk and data-protection policies.
          </p>
          <Link to="/"
            data-testid="contact-success-home-btn"
            className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#C9A86A] hover:text-[#E0C28A] transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0B0B0B] text-white" data-testid="contact-page">
      {/* Hero — extends behind the transparent fixed header on this route */}
      <header className="relative px-6 pt-32 pb-12 md:pt-40 md:pb-20 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link to="/"
            data-testid="contact-back-home-btn"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-slate-400 hover:text-[#C9A86A] transition-colors mb-6">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>
          <div className="flex items-center gap-3 mb-6">
            <ShieldCheck className="w-7 h-7 text-[#C9A86A]" />
            <span className="text-[10px] uppercase tracking-[0.4em] text-[#C9A86A]">
              Grupo BMP · Institutional
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-serif leading-tight max-w-3xl">
            Let's start a <span className="text-[#C9A86A]">conversation</span>.
          </h1>
          <p className="mt-6 text-slate-300 text-lg max-w-2xl leading-relaxed">
            Whether you are exploring a partnership, supply contract,
            investment opportunity, or simply have a question — write to us
            below and our institutional desk will respond within one
            business day.
          </p>
        </div>
      </header>

      {/* Body — 2 columns */}
      <div className="max-w-6xl mx-auto px-6 py-12 md:py-20 grid md:grid-cols-3 gap-12">
        {/* Left: contact info — pulled from CMS (NewFooter) */}
        <aside className="md:col-span-1 space-y-6">
          <Block icon={Mail} title="Email"
            value="info@grupobmp.com" sub="General enquiries" />
          <Block icon={Phone} title="Phone"
            value="+971 504 710 240" sub="UAE office hours" />
          <Block icon={MapPin} title="Headquarters"
            value="Shams Media City, Sharjah · UAE"
            sub="Group HQ" />
          <Block icon={MapPin} title="Africa Operations"
            value="Kilamba, Edifício W15, Luanda"
            sub="+244 923 130 100" />
          <Block icon={Globe} title="Compliance"
            value="UAE Federal Tax · DED Licensed"
            sub="FATF-aligned KYC/AML" />

          <div className="mt-10 pt-6 border-t border-white/10 text-xs text-slate-400 leading-relaxed">
            All enquiries are treated under our institutional data-protection
            standards. We may screen submissions for compliance (PEP, sanctions)
            before contacting you back.
          </div>
        </aside>

        {/* Right: form */}
        <form onSubmit={submit} className="md:col-span-2 space-y-5"
          data-testid="contact-form">
          <SectionTitle icon={Building2} title="Tell us about you" />
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Full name *" value={data.name} onChange={set('name')}
              testid="contact-input-name" />
            <Field label="Email *" type="email" value={data.email}
              onChange={set('email')} testid="contact-input-email" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Phone" value={data.phone} onChange={set('phone')}
              testid="contact-input-phone" />
            <Field label="Company / Organisation *" value={data.company}
              onChange={set('company')} testid="contact-input-company" />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Country" value={data.country} onChange={set('country')}
              testid="contact-input-country" placeholder="e.g. United Arab Emirates" />
            <Select label="I am a…" value={data.role} onChange={set('role')}
              options={[
                { value: 'client', label: 'Potential Client' },
                { value: 'supplier', label: 'Supplier' },
                { value: 'investor', label: 'Investor' },
                { value: 'partner', label: 'Partner / Reseller' },
                { value: 'other', label: 'Other' },
              ]} testid="contact-select-role" />
            <Field label="Industry" value={data.industry}
              onChange={set('industry')} testid="contact-input-industry"
              placeholder="e.g. trading" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-slate-400">
              Message
            </label>
            <textarea
              data-testid="contact-input-message"
              rows={5}
              className="mt-2 w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-[#C9A86A] focus:outline-none transition-colors text-white placeholder:text-slate-500 resize-none"
              value={data.message} onChange={set('message')}
              placeholder="Briefly describe your enquiry…"
            />
          </div>

          {/* Honeypot — hidden visually + from screen readers */}
          <input
            type="text" name="website_url" tabIndex="-1" autoComplete="off"
            value={data.hp_field} onChange={set('hp_field')}
            style={{ position: 'absolute', left: '-9999px', height: 0, width: 0 }}
            aria-hidden="true"
          />

          {error && (
            <div className="px-4 py-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-sm flex items-start gap-2"
              data-testid="contact-error">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="pt-4">
            <button type="submit" disabled={loading}
              data-testid="contact-submit-btn"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg bg-[#C9A86A] hover:bg-[#B89860] text-black font-medium tracking-wide transition-all disabled:opacity-50 group">
              {loading
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Send className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />}
              {loading ? 'Sending…' : 'Send enquiry'}
            </button>
            <p className="mt-3 text-[11px] text-slate-500">
              By submitting you confirm the information is accurate. Your
              data is processed solely for evaluating your enquiry.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

const Block = ({ icon: Icon, title, value, sub }) => (
  <div className="flex items-start gap-3">
    <div className="p-2 rounded-lg bg-[#C9A86A]/10 border border-[#C9A86A]/30">
      <Icon className="w-4 h-4 text-[#C9A86A]" />
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400">{title}</div>
      <div className="text-sm text-white mt-0.5">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  </div>
);

const SectionTitle = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2 pb-2 border-b border-white/10">
    <Icon className="w-4 h-4 text-[#C9A86A]" />
    <h2 className="text-sm uppercase tracking-[0.2em] text-slate-300">{title}</h2>
  </div>
);

const Field = ({ label, value, onChange, type = 'text', testid, placeholder }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.2em] text-slate-400">{label}</label>
    <input
      type={type}
      data-testid={testid}
      className="mt-2 w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-[#C9A86A] focus:outline-none transition-colors text-white placeholder:text-slate-500"
      value={value} onChange={onChange} placeholder={placeholder}
    />
  </div>
);

const Select = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.2em] text-slate-400">{label}</label>
    <select
      data-testid={testid}
      className="mt-2 w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-[#C9A86A] focus:outline-none transition-colors text-white"
      value={value} onChange={onChange}>
      {options.map((o) => (
        <option key={o.value} value={o.value} className="bg-[#0B0B0B]">
          {o.label}
        </option>
      ))}
    </select>
  </div>
);

export default ContactPage;
